/** XP per fight (docs/game-design.md §2). */
import { PROGRESSION } from '../config/progression';

export type Outcome = 'win' | 'loss' | 'draw';

export interface XpInput {
  outcome: Outcome;
  /** The fight ended by KO (gives the winner the KO bonus). */
  ko: boolean;
  /** Hidden rating difference, opponent − player. Bots count as an even match. */
  ratingDiff: number;
  vsBot: boolean;
  /** 1-based number of this fight in the weekly budget (non-league fights only). */
  fightNumberThisWeek: number;
  rested: boolean;
  firstWinOfDay: boolean;
  streakReward: boolean;
  /** Set for league fights: no budget, no rested bonus, half XP when a game plan fought. */
  league?: { playedLive: boolean } | null;
}

export type XpLineKey =
  | 'win'
  | 'loss'
  | 'draw'
  | 'ko'
  | 'opponent'
  | 'band'
  | 'bot'
  | 'rested'
  | 'leagueAway'
  | 'firstWin'
  | 'streak';

export interface XpLine {
  key: XpLineKey;
  /** For 'add' lines the XP added; for 'mul' lines the multiplier. */
  value: number;
  kind: 'add' | 'mul';
}

export interface XpResult {
  lines: XpLine[];
  /** XP before bonuses (rounded). */
  fightXp: number;
  bonus: number;
  total: number;
  opponentMultiplier: number;
  bandFactor: number;
}

export function opponentBand(ratingDiff: number) {
  const bands = PROGRESSION.opponentBands;
  for (const b of bands) if (ratingDiff <= b.maxDiff) return b;
  return bands[bands.length - 1]!;
}

export function weeklyBandFactor(fightNumber: number): number {
  for (const b of PROGRESSION.weeklyBands) if (fightNumber <= b.upTo) return b.factor;
  return 0;
}

export function calcFightXp(i: XpInput): XpResult {
  const X = PROGRESSION.xp;
  const lines: XpLine[] = [];
  const win = i.outcome === 'win';
  const opp = win && !i.vsBot ? opponentBand(i.ratingDiff).multiplier : 1;

  let base: number;
  if (win) {
    lines.push({ key: 'win', value: X.win, kind: 'add' });
    base = X.win;
    if (i.ko) {
      lines.push({ key: 'ko', value: X.koBonus, kind: 'add' });
      base += X.koBonus;
    }
    if (opp !== 1) lines.push({ key: 'opponent', value: opp, kind: 'mul' });
    base *= opp;
  } else {
    base = i.outcome === 'draw' ? X.draw : X.loss;
    lines.push({ key: i.outcome, value: base, kind: 'add' });
  }

  const bonusLines: XpLine[] = [];
  if (win && i.firstWinOfDay) bonusLines.push({ key: 'firstWin', value: X.firstWinOfDay, kind: 'add' });
  if (i.streakReward) bonusLines.push({ key: 'streak', value: X.streakReward, kind: 'add' });

  let fightXp: number;
  let bandFactor = 1;
  if (i.league) {
    const f = i.league.playedLive ? 1 : X.leagueAwayFactor;
    if (f !== 1) lines.push({ key: 'leagueAway', value: f, kind: 'mul' });
    fightXp = Math.round(base * f);
    const bonus = i.league.playedLive ? bonusLines.reduce((a, l) => a + l.value, 0) : 0;
    if (i.league.playedLive) lines.push(...bonusLines);
    return { lines, fightXp, bonus, total: fightXp + bonus, opponentMultiplier: opp, bandFactor };
  }

  bandFactor = weeklyBandFactor(i.fightNumberThisWeek);
  let xp = base;
  if (bandFactor !== 1) {
    lines.push({ key: 'band', value: bandFactor, kind: 'mul' });
    xp *= bandFactor;
  }
  if (i.vsBot) {
    lines.push({ key: 'bot', value: X.botFactor, kind: 'mul' });
    xp *= X.botFactor;
  }
  if (i.rested) {
    lines.push({ key: 'rested', value: PROGRESSION.rested.factor, kind: 'mul' });
    xp *= PROGRESSION.rested.factor;
  }
  fightXp = Math.round(xp);
  lines.push(...bonusLines);
  const bonus = bonusLines.reduce((a, l) => a + l.value, 0);
  return { lines, fightXp, bonus, total: fightXp + bonus, opponentMultiplier: opp, bandFactor };
}

/**
 * A rating difference that stands for `ratingDiff`'s opponent band (docs/game-design.md §2.2).
 * The server sends this instead of real ratings, which stay hidden from clients (§6).
 */
export function bandRatingDiff(ratingDiff: number): number {
  const bands = PROGRESSION.opponentBands;
  const i = bands.indexOf(opponentBand(ratingDiff));
  if (i < bands.length - 1) return bands[i]!.maxDiff;
  return (bands[bands.length - 2]?.maxDiff ?? 0) + 100;
}
